﻿// © Microsoft Corporation. All rights reserved.

import { IconHandledProps, IconProps, IconUnhandledProps } from "./Icon.props";

import Foundation from "@microsoft/fast-components-foundation-react";
import { Glyph } from ".";
import { HandledPropsKeys } from "@msnews/core";
import React from "react";

/**
 * Icon component
 * @class - Icon
 * @classdesc - The Icon component. Renders the image with respective glyph icon
 * @default
 */
export class Icon extends Foundation<IconHandledProps, IconUnhandledProps, {}> {
    /**
     * Component HandledProps enumeration. Anything not enumerated here will be included in the experience root HTML tag via unhandledProps()
     */
    protected handledProps: HandledPropsKeys<IconHandledProps> = {
        ariaLabel: undefined,
        fill: undefined,
        glyphType: undefined,
        height: undefined,
        managedClasses: undefined,
        onError: undefined,
        width: undefined,
        wrapperClassName: undefined
    };

    constructor(props: IconProps) {
        super(props);
    }

    /**
     * Component renderer
     * @public
     * @method
     * @returns {JSX.Element} the image component containing a glyph icon
     */
    public render(): JSX.Element {
        const { ariaLabel, fill, height, managedClasses, width, glyphType, wrapperClassName } = this.props;
        if (!glyphType) {
            return (null);
        }
        const role = ariaLabel ? "img" : "presentation";

        if (typeof glyphType === "string") {
            // when glyphType is string, use img tag
            let icon = glyphType as string;
            if (Glyph[icon]) {
                icon = Glyph[icon];
            }
            return (<img src={icon}
                style={{ width, height }}
                className={this.generateClassNames(managedClasses.icon)}
                title={ariaLabel || null}
                aria-label={ariaLabel || null}
                role = {role}
                aria-hidden={!ariaLabel} />);
        }

        const IconComponent = React.lazy(() => glyphType()
                                            .then(module => ({default: module.ReactComponent}))
                                            .catch(this.fallBackInlineComponent));

        const attrProps: React.SVGAttributes<SVGSVGElement> = {};
        if (width) {
            attrProps.width = width;
        }
        if (height) {
            attrProps.height = height;
        }

        return (
            <span
                title={ariaLabel || null}
                aria-label={ariaLabel || null}
                role={role}
                aria-hidden={!ariaLabel}
                className={wrapperClassName}>
                <React.Suspense fallback={<span />} >
                    <IconComponent fill={fill} className={this.generateClassNames(managedClasses.icon_inline)} {...attrProps} />
                </React.Suspense>
            </span>
        );
    }

    /**
     * fallback element when inline fetch fails.
     */
    private fallBackInlineComponent = () => ({default: () => <span />});
}